import { motion, useReducedMotion } from 'framer-motion'
import { useEffect, useState, type CSSProperties } from 'react'
import type { SupportedLocale } from '../../types/simulation'
import { onboardingIdeaLabels, studioQuickStartIdeasLabel } from '../../i18n/studioQuickStartIdeas'
import { haptic } from '../../lib/native'

export const ONBOARDING_PREMIUM_IDEAS = [
  { idea: 6, x: 50, y: 8, width: 84, angle: -2, font: 3.7, surface: 'paper', typeface: 'serif' },
  { idea: 19, x: 51, y: 18, width: 78, angle: 2, font: 3.8, surface: 'lavender', typeface: 'sans' },
  { idea: 10, x: 43, y: 28, width: 66, angle: -2, font: 4.2, surface: 'coral', typeface: 'serif' },
  { idea: 5, x: 60, y: 38, width: 56, angle: 2, font: 4.7, surface: 'ink', typeface: 'mono' },
  { idea: 21, x: 42, y: 48, width: 62, angle: -1, font: 4.4, surface: 'mint', typeface: 'sans' },
  { idea: 8, x: 60, y: 58, width: 60, angle: 2, font: 4.2, surface: 'butter', typeface: 'serif' },
  { idea: 12, x: 42, y: 68, width: 70, angle: -2, font: 3.9, surface: 'silver', typeface: 'mono' },
  { idea: 16, x: 57, y: 78, width: 70, angle: 1, font: 3.9, surface: 'rose', typeface: 'sans' },
  { idea: 26, x: 38, y: 87, width: 66, angle: -1, font: 3.9, surface: 'paper', typeface: 'serif' },
  { idea: 36, x: 60, y: 95, width: 64, angle: 1, font: 3.9, surface: 'blue', typeface: 'sans' },
] as const

export function OnboardingIdeaCollage({ locale }: { locale: SupportedLocale }) {
  const reduceMotion = useReducedMotion()
  const [visibleCount, setVisibleCount] = useState(0)

  useEffect(() => {
    const timeouts: number[] = []
    const reveal = (count: number) => {
      setVisibleCount(count)
      void haptic('soft')
      // Match the impact to the card's first contact before its small rebound.
      timeouts.push(window.setTimeout(() => { void haptic('medium') }, 250))
    }
    timeouts.push(window.setTimeout(() => reveal(1), 180))
    for (let index = 1; index < ONBOARDING_PREMIUM_IDEAS.length; index += 1) {
      timeouts.push(window.setTimeout(() => reveal(index + 1), 180 + index * 1200))
    }
    return () => timeouts.forEach((timeout) => window.clearTimeout(timeout))
  }, [])

  return (
    <div className="onboarding-idea-collage" role="region" aria-label={studioQuickStartIdeasLabel[locale]}>
      <div className="onboarding-idea-floor" aria-hidden="true" />
      {ONBOARDING_PREMIUM_IDEAS.slice(0, visibleCount).map((item, index) => (
        <div
          key={item.idea}
          className="onboarding-idea-position"
          style={{
            left: `${item.x}%`, top: `${item.y}%`, width: `${item.width}%`, zIndex: index + 1,
            '--idea-size': `${item.font}cqw`,
          } as CSSProperties}
        >
          <motion.div
            className={`onboarding-idea-card surface-${item.surface} typeface-${item.typeface}`}
            data-idea-index={item.idea}
            initial={reduceMotion
              ? { opacity: 0, rotate: item.angle }
              : { opacity: 0, y: -100, x: index % 2 ? 22 : -22, rotate: item.angle - 3, scaleX: 0.94, scaleY: 0.94 }}
            animate={reduceMotion
              ? { opacity: 1, rotate: item.angle }
              : {
                  opacity: [0, 1, 1, 1, 1],
                  y: [-100, 3, -11, 1, 0],
                  x: [index % 2 ? 22 : -22, 0, 0, 0, 0],
                  rotate: [item.angle - 3, item.angle + 1, item.angle - 0.5, item.angle + 0.2, item.angle],
                  scaleX: [0.94, 1.018, 0.995, 1.004, 1],
                  scaleY: [0.94, 0.97, 1.01, 0.995, 1],
                }}
            transition={reduceMotion
              ? { duration: 0.18 }
              : { duration: 0.64, times: [0, 0.39, 0.66, 0.86, 1], ease: 'easeOut' }}
          >
            <span>{onboardingIdeaLabels[locale][index]}</span>
          </motion.div>
        </div>
      ))}
      {!reduceMotion && visibleCount > 0 && (
        <motion.div
          key={`impact-${visibleCount}`}
          className="onboarding-idea-impact"
          aria-hidden="true"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: [0, 0.2, 0], scale: [0.6, 1, 1.15], y: [0, 2, -1, 0] }}
          transition={{ delay: 0.25, duration: 0.3 }}
        />
      )}
    </div>
  )
}
